import type { ResolvedSalesOrder, ResolvedSalesOrderItem } from "../types/sales";
import { orderLineKey } from "utils/orderResolveLinePricing";
import { isOrderLineObjectId, normalizeOrderLineObjectId, salesLineIdentityKey } from "utils/orderLineObjectId";
import { round2 } from "utils/fixedAmountDiscountAllocation";

function resolvedOrderChargesHaveRows(
  resolved: ResolvedSalesOrder | null | undefined
): boolean {
  const charges = resolved?.order_charges?.[0];
  if (!charges) return false;
  return (
    (Array.isArray(charges.shipping_option) && charges.shipping_option.length > 0) ||
    (Array.isArray(charges.specialfee) && charges.specialfee.length > 0)
  );
}

/**
 * Overlay POST/PATCH sales order financial fields from last resolve response.
 * Structural fields (buyer, addresses, project) stay from the page builder.
 */
export function overlaySalesSubmitFromResolved(
  payload: Record<string, unknown>,
  resolved: ResolvedSalesOrder | null | undefined
): Record<string, unknown> {
  if (!resolved) return payload;

  const out = { ...payload };
  const rootDiscountCode = String(resolved.discount_code ?? "").trim();
  const lineCouponTotal = (resolved.items || []).reduce(
    (s, li) => s + Number(li.discount_on_total_quantity ?? 0),
    0
  );
  const rootDiscountAmount = round2(
    Number(resolved.discount_amount ?? 0) || lineCouponTotal
  );

  if (resolved.subtotal_amount != null) out.subtotal_amount = resolved.subtotal_amount;
  const feeSum = (resolved.order_charges?.[0]?.specialfee ?? []).reduce(
    (s, f) => s + Number(f.specialfee_amount ?? 0),
    0
  );
  const payloadShipping = Number(payload.shipping_amount ?? NaN);
  const shippingAmount =
    Number.isFinite(payloadShipping) && payloadShipping >= 0
      ? payloadShipping
      : Number(resolved.shipping_amount ?? 0);
  const taxAmount = Number(resolved.order_tax_summary?.over_all_tax_amount ?? 0);
  const catalogSubtotal = (resolved.items || []).reduce(
    (s, li) =>
      s + Number(li.unit_retail_price ?? 0) * Math.max(1, Number(li.quantity ?? 1)),
    0
  );
  out.total_amount = round2(
    catalogSubtotal - rootDiscountAmount + shippingAmount + feeSum + taxAmount
  );
  out.shipping_amount = shippingAmount;
  out.discount_code = rootDiscountCode;
  out.discount_amount = rootDiscountAmount;

  if (resolved.order_tax_summary) {
    const existingSummary =
      typeof out.order_tax_summary === "object" && out.order_tax_summary
        ? (out.order_tax_summary as Record<string, unknown>)
        : {};
    // Caller-set tax_status (e.g. "complete" on Mark as Delivered) wins over resolve echo ("taxcloud").
    const preservedTaxStatus = String(existingSummary.tax_status ?? "").trim();
    const resolvedTaxStatus = String(resolved.order_tax_summary.tax_status ?? "").trim();
    out.order_tax_summary = {
      ...existingSummary,
      tax_inclusive: resolved.order_tax_summary.tax_inclusive ?? false,
      over_all_tax_amount: resolved.order_tax_summary.over_all_tax_amount ?? 0,
      over_all_tax_percentage: resolved.order_tax_summary.over_all_tax_percentage ?? 0,
      over_all_taxable_base: resolved.order_tax_summary.over_all_taxable_base ?? 0,
      tax_status: preservedTaxStatus || resolvedTaxStatus || "",
    };
    out.tax_amount = resolved.order_tax_summary.over_all_tax_amount ?? 0;
  }

  if (resolved.cart_id != null) {
    out.cart_id = resolved.cart_id;
  }

  if (resolvedOrderChargesHaveRows(resolved)) {
    out.order_charges = resolved.order_charges;
  }

  const resolvedItems = resolved.items || [];
  const usedResolved = new Set<number>();
  const takeResolvedLine = (item: Record<string, unknown>, index: number): ResolvedSalesOrderItem | undefined => {
    const itemId = normalizeOrderLineObjectId(item._id ?? item.line_item_id);
    if (isOrderLineObjectId(itemId)) {
      const found = resolvedItems.findIndex(
        (li, i) => !usedResolved.has(i) && normalizeOrderLineObjectId(li._id) === itemId
      );
      if (found >= 0) {
        usedResolved.add(found);
        return resolvedItems[found];
      }
    }
    if (!usedResolved.has(index) && resolvedItems[index]) {
      usedResolved.add(index);
      return resolvedItems[index];
    }
    const key = salesLineIdentityKey(item) || orderLineKey(String(item.product_id ?? ""), String(item.variant_id ?? ""));
    const foundPv = resolvedItems.findIndex((li, i) => {
      if (usedResolved.has(i)) return false;
      return salesLineIdentityKey(li) === key;
    });
    if (foundPv >= 0) {
      usedResolved.add(foundPv);
      return resolvedItems[foundPv];
    }
    return undefined;
  };

  if (Array.isArray(out.items)) {
    out.items = (out.items as Record<string, unknown>[]).map((item, index) => {
      const r = takeResolvedLine(item, index);
      if (!r) return item;

      const isCustom = String(item.inventory_source ?? r.inventory_source ?? "") === "custom";
      const baseTaxInfo = (r.item_tax_info ?? item.item_tax_info ?? {}) as Record<string, unknown>;
      const payloadTaxInfo = (item.item_tax_info ?? {}) as Record<string, unknown>;
      const isTaxable =
        typeof item.is_taxable === "boolean"
          ? item.is_taxable
          : typeof r.is_taxable === "boolean"
            ? r.is_taxable
            : typeof payloadTaxInfo.taxable === "boolean"
              ? (payloadTaxInfo.taxable as boolean)
              : typeof baseTaxInfo.taxable === "boolean"
                ? (baseTaxInfo.taxable as boolean)
                : true;

      const merged: Record<string, unknown> = {
        ...item,
        unit_retail_price: r.unit_retail_price ?? item.unit_retail_price,
        changed_unit_price: r.changed_unit_price ?? item.changed_unit_price,
        is_price_overridden:
          typeof r.is_price_overridden === "boolean"
            ? r.is_price_overridden
            : item.is_price_overridden,
        item_sub_total: r.item_sub_total ?? item.item_sub_total,
        total_price: r.total_price ?? item.total_price,
        is_taxable: isTaxable,
        item_tax_info: {
          ...baseTaxInfo,
          taxable: isTaxable,
        },
      };

      // Never drop existing line-item Mongo _id when overlaying resolve totals.
      const payloadLineId = String(item._id ?? item.line_item_id ?? "").trim();
      const resolvedLineId = String((r as { _id?: unknown })._id ?? "").trim();
      const keepId = payloadLineId || resolvedLineId;
      if (keepId && keepId !== "000000000000000000000000") {
        merged._id = keepId;
      }

      if (!isCustom) {
        merged.discount_on_unit_quantity = r.discount_on_unit_quantity ?? 0;
        merged.discount_on_total_quantity = r.discount_on_total_quantity ?? 0;
      } else {
        merged.discount_on_unit_quantity = 0;
        merged.discount_on_total_quantity = 0;
      }

      return merged;
    });
  }

  return out;
}
